import React, { useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import { EMPTY_MESSAGES, PLACEHOLDER_MESSAGES, VALIDATION_MESSAGES } from '@/shared/lib/constants';
import { sortByDisplayName } from '@/shared/lib/sort/sortByDisplayName';
import type { DomainTreeItem } from '@/shared/types/domain';
import { Chip } from '@/shared/ui/Chip';
import { EditableTreeSelect } from '@/shared/ui/EditableTreeSelect';
import type { TreeSelectNode } from '@/shared/ui/EditableTreeSelect/types';
import { Icon } from '@/shared/ui/Icon';

import type { DomainHierarchyItem } from '@/entities/domain/domain';
import { useDomainHierarchy } from '@/entities/domain/model';

type DomainTreeProps = {
  title: string;
  domainData: DomainTreeItem[];
  onSave: (values: string[]) => void;
  placeholder?: string;
  emptyText?: string;
  limit?: number;
  className?: string;
  disabled?: boolean;
  isUpdating?: boolean;
};

const transformDomainHierarchyToTreeNodes = (items: DomainHierarchyItem[]): TreeSelectNode[] => {
  const sortedItems = sortByDisplayName(items);

  return sortedItems.map((item) => {
    const node: TreeSelectNode = {
      key: item.id,
      value: item.id,
      title: item.displayName || item.name,
      description: item.description,
      children:
        item.children && item.children.length > 0 ? transformDomainHierarchyToTreeNodes(item.children) : undefined,
      isLeaf: !item.children || item.children.length === 0,
    };
    return node;
  });
};

export const DomainTree: React.FC<DomainTreeProps> = ({
  title,
  domainData,
  onSave,
  placeholder = PLACEHOLDER_MESSAGES.DOMAIN_SELECT,
  emptyText = EMPTY_MESSAGES.NO_DOMAIN,
  limit = 3000,
  className,
  disabled = false,
  isUpdating = false,
}) => {
  const navigate = useNavigate();

  const { data: domainHierarchyData, isLoading } = useDomainHierarchy({ limit });

  const value = domainData.map((domain) => domain.id);
  const treeData = useMemo(() => {
    if (!domainHierarchyData?.data) return [];
    return transformDomainHierarchyToTreeNodes(domainHierarchyData.data);
  }, [domainHierarchyData]);

  const renderDisplay = (values: string[]) => {
    const displayDomains = domainData.filter((domain) => values.includes(domain.id));
    if (displayDomains.length === 0) return null;

    const domain = displayDomains[0];

    return (
      <Chip
        icon={<Icon name="globe" size={12} />}
        label={domain.displayName || domain.name}
        onClick={() => {
          if (domain.fullyQualifiedName) {
            navigate(`/domain/${encodeURIComponent(domain.fullyQualifiedName)}/sub-domains`);
          }
        }}
      />
    );
  };

  return (
    <EditableTreeSelect
      title={title}
      value={value}
      treeData={treeData}
      isLoading={isLoading}
      isUpdating={isUpdating}
      placeholder={placeholder}
      emptyText={emptyText}
      onSave={onSave}
      multiple={false}
      className={className}
      renderDisplay={renderDisplay}
      disabled={disabled}
      showTooltip={true}
      popupStyles={{
        maxHeight: 300,
        overflow: 'auto',
      }}
      tooltipPlacement="left"
      searchValidation={{
        maxLength: 30,
        warningMessage: VALIDATION_MESSAGES.DOMAIN_SEARCH_MAX_LENGTH,
      }}
    />
  );
};
